import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../App';
import { Megaphone, PlusCircle, Users, Trash2, Eye } from 'lucide-react';

export default function MyBriefs() {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchBriefs = async () => {
      try {
        const response = await api.get('/briefs/my');
        setBriefs(response.data || []);
      } catch (error) {
        console.error('Error fetching briefs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBriefs();
  }, []);
  
  const deleteBrief = async (briefId) => {
    if (!window.confirm('Are you sure you want to delete this brief?')) return;
    try {
      await api.delete(`/briefs/${briefId}`);
      setBriefs(prev => prev.filter(b => b.brief_id !== briefId));
    } catch (error) {
      alert('Failed to delete brief');
    }
  };
  
  const formatBudget = (min, max) => {
    if (!min && !max) return null;
    const formatNum = (n) => n >= 100000 ? `₹${(n/100000).toFixed(1)}L` : `₹${(n/1000).toFixed(0)}K`;
    if (min && max) return `${formatNum(min)} - ${formatNum(max)}`;
    if (max) return `Up to ${formatNum(max)}`;
    return `From ${formatNum(min)}`;
  };
  
  if (loading) {
    return (
      <DashboardLayout title="My Briefs">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0D7377]"></div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout title="My Briefs">
      <div className="flex justify-end mb-6">
        <Link to="/client/briefs/new" className="btn-client px-6 py-2.5 flex items-center gap-2">
          <PlusCircle size={18} />
          Post New Brief
        </Link>
      </div>
      
      {briefs.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-12 text-center">
          <Megaphone className="text-neutral-300 mx-auto mb-4" size={48} />
          <p className="text-neutral-500 mb-4">You haven't posted any briefs yet</p>
          <Link to="/client/briefs/new" className="btn-client px-6 py-3 inline-flex items-center gap-2">
            <PlusCircle size={18} />
            Post Your First Brief
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {briefs.map((brief) => (
            <div key={brief.brief_id} className="bg-white border border-neutral-200 p-6 card-client">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Link to={`/brief/${brief.brief_id}`} className="font-semibold text-lg hover:text-[#0D7377]">
                      {brief.title}
                    </Link>
                    <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${
                      brief.status === 'open' ? 'bg-green-100 text-green-700' :
                      brief.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-neutral-100 text-neutral-600'
                    }`}>
                      {brief.status}
                    </span>
                  </div>
                  
                  <p className="text-neutral-600 text-sm mb-3 line-clamp-2">{brief.description}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {brief.method && <span className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600">{brief.method}</span>}
                    {brief.sector && <span className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600">{brief.sector}</span>}
                    {formatBudget(brief.budget_min, brief.budget_max) && (
                      <span className="text-xs px-2 py-1 bg-neutral-100 text-neutral-600">
                        {formatBudget(brief.budget_min, brief.budget_max)}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col items-end gap-3">
                  <Link
                    to={`/brief/${brief.brief_id}`}
                    className="flex items-center gap-2 px-4 py-2 bg-[#E6F3F4] text-[#0D7377] font-medium text-sm"
                  >
                    <Users size={16} />
                    {brief.interest_count || 0} Interested
                  </Link>
                  
                  <div className="flex gap-2">
                    <Link to={`/brief/${brief.brief_id}`} className="p-2 text-neutral-400 hover:text-neutral-600">
                      <Eye size={18} />
                    </Link>
                    <button onClick={() => deleteBrief(brief.brief_id)} className="p-2 text-neutral-400 hover:text-red-500">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
